// Firestore writes shared with the native driver apps. Keep these patches flat:
// replacing `config` would delete unrelated driver settings.
export const SKIP_COOLDOWN_MS = 10000;
export const TRACK_CHANGE_COOLDOWN_MS = 3000;
export const QUEUE_COOLDOWN_MS = 60000;
export const PASSENGER_STALE_MS = 240000;

export const DEFAULT_REMOTE_SETTINGS = Object.freeze({
  autoSkipEnabled: true,
  autoSkipSeconds: 45,
  minAutoSkipSeconds: 45,
  maxAutoSkipSeconds: 150,
  volumePercent: 50,
  minVolumePercent: 5,
  maxVolumePercent: 80,
});

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const positiveInteger = value => Number.isInteger(value) && value > 0;
const finiteNumber = value => typeof value === 'number' && Number.isFinite(value);

export function readRemoteSettings(userData = {}, metaConfig = {}) {
  const user = isRecord(userData) ? userData : {};
  const config = isRecord(user.config) ? user.config : {};
  const meta = isRecord(metaConfig) ? metaConfig : {};
  const defaults = DEFAULT_REMOTE_SETTINGS;
  const readBound = (key, fallback) => positiveInteger(config[key]) ? config[key]
    : positiveInteger(meta[key]) ? meta[key] : fallback;
  const minAutoSkipSeconds = readBound('min_autoskip', defaults.minAutoSkipSeconds);
  const maxAutoSkipSeconds = Math.max(minAutoSkipSeconds,
    readBound('max_autoskip', defaults.maxAutoSkipSeconds));
  const seconds = positiveInteger(config.autoSkipAt) ? config.autoSkipAt : defaults.autoSkipSeconds;
  // iOS consumes Meta/config.min_volume as a normalized float, not a percentage.
  const minVolumePercent = finiteNumber(meta.min_volume) && meta.min_volume >= 0 && meta.min_volume <= 1
    ? Math.ceil(meta.min_volume * 100) : defaults.minVolumePercent;
  const volumePercent = finiteNumber(user.pax_volume) && user.pax_volume >= 0 && user.pax_volume <= 1
    ? Math.round(user.pax_volume * 100) : defaults.volumePercent;
  // Android caps passenger commands at Meta/config.max_pax_volume (normalized, default 0.80).
  // The driver's own hardware level may sit above it, so the displayed value is not clamped.
  const maxVolumePercent = Math.max(minVolumePercent,
    finiteNumber(meta.max_pax_volume) && meta.max_pax_volume >= 0 && meta.max_pax_volume <= 1
      ? Math.floor(meta.max_pax_volume * 100) : defaults.maxVolumePercent);
  return {
    autoSkipEnabled: typeof config.isAutoSkipOn === 'boolean' ? config.isAutoSkipOn : defaults.autoSkipEnabled,
    autoSkipSeconds: Math.min(maxAutoSkipSeconds, Math.max(minAutoSkipSeconds, seconds)),
    minAutoSkipSeconds,
    maxAutoSkipSeconds,
    volumePercent: Math.max(minVolumePercent, volumePercent),
    minVolumePercent,
    maxVolumePercent,
  };
}

export function buildVolumePatch(percent, settings = DEFAULT_REMOTE_SETTINGS) {
  const minimum = settings.minVolumePercent;
  const maximum = finiteNumber(settings.maxVolumePercent) ? settings.maxVolumePercent : 100;
  if (!finiteNumber(minimum) || minimum < 0 || minimum > 100 || maximum < minimum || maximum > 100 ||
      !finiteNumber(percent) || percent < minimum || percent > maximum) {
    throw new RangeError('Volume must be a number within the driver volume limits.');
  }
  return { pax_volume: percent / 100 };
}

export function buildAutoSkipPatch(enabled, seconds, settings = DEFAULT_REMOTE_SETTINGS) {
  if (typeof enabled !== 'boolean') {
    throw new TypeError('Auto-skip enabled must be a boolean.');
  }
  // Off retains the last usable duration for the next time auto-skip is enabled.
  if (!enabled) return { 'config.isAutoSkipOn': false };
  const minimum = settings.minAutoSkipSeconds;
  const maximum = settings.maxAutoSkipSeconds;
  if (!positiveInteger(minimum) || !positiveInteger(maximum) || maximum < minimum ||
      !positiveInteger(seconds) || seconds < minimum || seconds > maximum) {
    throw new RangeError('Auto-skip seconds must be an integer within the driver limits.');
  }
  return { 'config.isAutoSkipOn': true, 'config.autoSkipAt': seconds };
}

export function buildSkipPatch() {
  // Native iOS consumes a boolean command and acknowledges it by writing false.
  return { remoteSkip: true };
}
